import Card from "../../UI/Card";
import InlineEditableText from "../../UI/InlineEditableText";
import Text from "../../UI/Text";
import Title from "../../UI/Title";
// @ts-expect-error pure js
import ToggleSwitch from "../../UI/ToggleSwitch";
// @ts-expect-error pure js
import useTenants from "../../../hooks/use-tenants";
// @ts-expect-error pure js
import useProperties from "../../../hooks/use-properties";
// @ts-expect-error pure js
import { currencyFormatter } from "../../../api/Functions";
// @ts-expect-error pure js
import { handleAPIError } from "../../../utils";
import { useToast } from "../../UI/Toast/useToast";
import type { Tenant } from "../../../models/tenant";
import type { Property } from "../../../models/property";
import styles from "./AplConfigCard.module.css";

export default function AplConfigCard({ tenantId }: { tenantId: number }) {
  const toast = useToast();
  const { tenantsById, updateTenant } = useTenants({ withById: true }) as {
    tenantsById: Record<number, Tenant>;
    updateTenant: (id: number, values: Partial<Tenant>) => Promise<void>;
  };
  const { propertiesById } = useProperties({ withById: true }) as {
    propertiesById: Record<number, Property>;
  };

  const tenant = tenantsById[tenantId];
  if (!tenant) return null;

  const profileId = propertiesById[tenant.id_bien]?.id_profile_nom_owner;

  async function save(values: Partial<Tenant>) {
    try {
      await updateTenant(tenantId, values);
      return true;
    } catch (error) {
      toast.error({
        title: "Impossible d'enregistrer l'aide au logement",
        description: handleAPIError(error),
      });
      return false;
    }
  }

  return (
    <Card padding="md" className={styles.card}>
      <div className={styles.cardHeader}>
        <Title variant="deca" weight="bold">
          Aide au logement
        </Title>
        <Text>
          Indiquez si le locataire perçoit une aide au logement et précisez les
          informations utiles.
        </Text>
      </div>

      <div className={styles.content}>
        <div className={styles.toggleRow}>
          <Text weight="medium">
            Le locataire perçoit une aide au logement (CAF, APL, MSA)
          </Text>
          <ToggleSwitch
            status={!!tenant.caf_aide}
            onToggle={(e: React.ChangeEvent<HTMLInputElement>) =>
              save({
                caf_aide: e.target.checked,
                caf_deduction: e.target.checked,
              })
            }
            requiresWrite
            profileId={profileId}
          />
        </div>

        {tenant.caf_aide && (
          <>
            <div className={styles.fields}>
              <div className={styles.field}>
                <Text variant="deci">Montant des APL</Text>
                <InlineEditableText
                  label="Montant des APL"
                  type="number"
                  step="0.01"
                  unit="€"
                  placeholder="250"
                  value={tenant.montant_caf}
                  format={(value) => currencyFormatter(Number(value))}
                  onSave={(value) =>
                    save({
                      montant_caf: value === "" ? null : parseFloat(value),
                    } as Partial<Tenant>)
                  }
                  requiresWrite
                  profileId={profileId}
                />
              </div>
              <div className={styles.field}>
                <Text variant="deci">Numéro allocataire</Text>
                <InlineEditableText
                  label="Numéro allocataire"
                  placeholder="1234567A"
                  value={tenant.caf_numero_allocataire}
                  onSave={(value) =>
                    save({ caf_numero_allocataire: value.trim() })
                  }
                  requiresWrite
                  profileId={profileId}
                />
              </div>
            </div>
            <div className={styles.toggleRow}>
              <Text>
                Déduire automatiquement le montant de l'APL du montant appelé
                dans l'avis d'échéance
              </Text>
              <ToggleSwitch
                status={!!tenant.caf_deduction}
                onToggle={(e: React.ChangeEvent<HTMLInputElement>) =>
                  save({ caf_deduction: e.target.checked })
                }
                requiresWrite
                profileId={profileId}
              />
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
